<template>
    <div @pointerover="onPointerover" @pointerleave="onPointerleave" ref="reference" v-bind="$attrs">
        <slot />
    </div>
    <Transition
        enter-active-class="duration-100 ease"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-100 ease"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
    >
        <div
            v-if="isOpen || forceShow"
            v-show="!middlewareData.hide?.referenceHidden"
            :style="{
                position: strategy,
                top: tooltipTop,
                left: tooltipLeft,
                visibility: hideTooltip ? 'hidden' : 'visible'
            }"
            :class="tooltipLabelClasses"
            class="tooltip"
            ref="tooltip"
        >
            <div :style="arrowStyle" class="absolute rotate-45 bg-inherit aspect-square w-2 pointer-events-none" :class="tooltipArrowClasses" ref="tooltipArrow">
                <div class="w-full h-full bg-inherit"></div>
            </div>
            <div class="relative z-[1]">
                <slot name="tooltip">{{ label }}</slot>
            </div>
        </div>
    </Transition>
</template>

<script setup lang="ts">
    import { ref, computed, useTemplateRef } from 'vue'
    import { useFloating, autoUpdate, arrow, flip, offset, type Placement, shift, hide, autoPlacement } from '@floating-ui/vue'

    const props = withDefaults(
        defineProps<{
            label?: string
            position?: 'up' | 'right' | 'down' | 'left'
            offset?: number
            forceShow?: boolean
            width?: 'md' | 'lg'
            variant?: 'dark' | 'neutral' | 'light'
            hideTooltip?: boolean
            allowAxisFlip?: boolean
        }>(),
        {
            forceShow: false,
            position: 'up',
            offset: 10,
            width: 'md',
            variant: 'dark',
            hideTooltip: false,
            allowAxisFlip: true
        }
    )

    const position = computed((): Placement => {
        const placementOptions = {
            up: 'top-center',
            right: 'right-center',
            down: 'bottom-center',
            left: 'left-center'
        }
        return placementOptions[props.position] as Placement
    })

    const isOpen = ref(false)
    const reference = useTemplateRef('reference')
    const tooltip = useTemplateRef('tooltip')
    const tooltipArrow = useTemplateRef('tooltipArrow')

    const fallbackAxisSideDirection = computed(() => {
        return props.allowAxisFlip ? 'end' : 'none'
    })

    const { x, y, strategy, placement, middlewareData } = useFloating(reference, tooltip, {
        placement: position.value,
        middleware: [
            offset(props.offset),
            flip({
                fallbackAxisSideDirection: fallbackAxisSideDirection.value
            }),
            shift({
                padding: 8
            }),
            arrow({
                element: tooltipArrow,
                padding: 8
            }),
            hide({
                padding: 8
            })
        ],
        // whileElementsMounted: autoUpdate
        whileElementsMounted(referenceEl, tooltipArrowEl, update) {
            const cleanup = autoUpdate(referenceEl, tooltipArrowEl, update, {
                animationFrame: true
            })
            return cleanup
        }
    })

    const side = computed(() => placement.value.split('-')[0])
    const tooltipTop = computed(() => `${y.value ?? 0}px`)
    const tooltipLeft = computed(() => `${x.value ?? 0}px`)

    const arrowStyle = computed(() => {
        if (!middlewareData.value.arrow) return {}

        const { x: arrowX, y: arrowY } = middlewareData.value.arrow
        return {
            top: arrowY != null ? `${arrowY}px` : '',
            left: arrowX != null ? `${arrowX}px` : ''
        }
    })

    const tooltipArrowClasses = computed(() => {
        const positionOptions: any = {
            top: '-bottom-1',
            right: '-left-1',
            bottom: '-top-1',
            left: '-right-1'
        }
        return positionOptions[side.value]
    })

    function onPointerover() {
        isOpen.value = true
    }

    function onPointerleave() {
        isOpen.value = false
    }

    const tooltipLabelClasses = computed(() => {
        const baseClasses = 'text-xs rounded px-3 py-2 max-w-28 text-center text-balance transition-opacity relative tabular-nums inline-flex drop-shadow-sm'
        const variantClasses = {
            dark: 'bg-system-dark text-system-light',
            neutral: 'bg-neutral-100 text-default',
            light: 'bg-system-light text-default'
        }
        const widthOptions = {
            md: 'max-w-28 w-max',
            lg: 'max-w-40 w-max'
        }

        return [baseClasses, variantClasses[props.variant], widthOptions[props.width]]
    })
</script>
